/**
 * Flattened sortable ids: group header markers + preset ids for @dnd-kit
 * For Media Devoted | Created By Ciro Autiero
 */

import { arrayMove } from '@dnd-kit/sortable';

import type { Preset } from '../../shared';

export const GROUP_START_PREFIX = 'GS:' as const;

export function isGroupStartId(id: string): boolean {
  return id.startsWith(GROUP_START_PREFIX);
}

export function firstPresetIdFromGroupStartSortableId(id: string): string | null {
  if (!isGroupStartId(id)) return null;
  return id.slice(GROUP_START_PREFIX.length);
}

export function groupStartSortableId(firstPresetId: string): string {
  return `${GROUP_START_PREFIX}${firstPresetId}`;
}

/** Display name for a preset group (sidebar / headers). */
export function presetGroupDisplayLabel(groupName: string | null | undefined): string {
  const t = groupName?.trim();
  return t && t.length > 0 ? t : 'Ungrouped';
}

/** Contiguous blocks matching list order (same as flattened group markers). */
export function groupPresetsContiguous(presets: Preset[]): Array<{
  groupKey: string;
  label: string;
  firstPresetId: string;
  items: Preset[];
}> {
  const out: Array<{ groupKey: string; label: string; firstPresetId: string; items: Preset[] }> = [];
  for (const p of presets) {
    const groupKey = p.groupName?.trim() ?? '';
    const label = presetGroupDisplayLabel(p.groupName);
    const last = out[out.length - 1];
    if (!last || last.groupKey !== groupKey) {
      out.push({ groupKey, label, firstPresetId: p.id, items: [p] });
    } else {
      last.items.push(p);
    }
  }
  return out;
}

export function buildFlattenedSortableIds(presets: Pick<Preset, 'id' | 'groupName'>[]): string[] {
  const out: string[] = [];
  let prev: string | null = null;
  for (const p of presets) {
    const g = p.groupName?.trim() ?? '';
    if (g !== prev) {
      out.push(groupStartSortableId(p.id));
      prev = g;
    }
    out.push(p.id);
  }
  return out;
}

export function presetIdsFromFlat(flat: string[]): string[] {
  return flat.filter((id) => !isGroupStartId(id));
}

function blockEndIndex(flat: string[], blockStart: number): number {
  let end = blockStart + 1;
  while (end < flat.length && !isGroupStartId(flat[end]!)) {
    end++;
  }
  return end;
}

function blockStartForPosition(flat: string[], index: number): number {
  let s = index;
  while (s > 0 && !isGroupStartId(flat[s]!)) {
    s--;
  }
  return s;
}

/**
 * Move a contiguous group block (GS + presets) relative to `overId`.
 * Returns null if invalid or no-op.
 */
export function moveGroupBlockFlat(
  flat: string[],
  activeHeaderId: string,
  overId: string,
): string[] | null {
  const aIdx = flat.indexOf(activeHeaderId);
  if (aIdx < 0 || !isGroupStartId(activeHeaderId)) return null;
  const aEnd = blockEndIndex(flat, aIdx);
  const overIdxOrig = flat.indexOf(overId);
  if (overIdxOrig < 0) return null;
  if (overIdxOrig > aIdx && overIdxOrig < aEnd) return null;

  const block = flat.slice(aIdx, aEnd);
  const rest = [...flat.slice(0, aIdx), ...flat.slice(aEnd)];

  const overInRest = rest.indexOf(overId);
  if (overInRest < 0) return null;

  let ins = overInRest;
  if (!isGroupStartId(rest[ins]!)) {
    ins = blockStartForPosition(rest, ins);
  }
  const movingDown = aIdx < overIdxOrig;
  const insertAt = movingDown ? blockEndIndex(rest, ins) : ins;

  return [...rest.slice(0, insertAt), ...block, ...rest.slice(insertAt)];
}

export function computeNewPresetOrderAfterDrag(
  presets: Pick<Preset, 'id' | 'groupName'>[],
  activeId: string,
  overId: string,
): string[] | null {
  if (activeId === overId) return null;
  const flat = buildFlattenedSortableIds(presets);

  if (isGroupStartId(activeId)) {
    const nextFlat = moveGroupBlockFlat(flat, activeId, overId);
    if (!nextFlat) return null;
    return presetIdsFromFlat(nextFlat);
  }

  const ids = presetIdsFromFlat(flat);
  const oi = ids.indexOf(activeId);
  const ni = ids.indexOf(overId);
  if (oi < 0 || ni < 0) return null;
  return arrayMove(ids, oi, ni);
}
